import { addDays, format, isValid, parseISO, subDays } from 'date-fns';

export interface ReportingPeriodRange {
  periodIndex: number;
  start: string;
  end: string;
}

/** Returns the first Thursday of the given calendar year (period 1 end date). */
export function getFirstThursdayOfYear(year: number): Date {
  const date = new Date(year, 0, 1);

  while (date.getDay() !== 4) {
    date.setDate(date.getDate() + 1);
  }

  return date;
}

/**
 * Returns the Friday–Thursday range for a reporting period.
 * Period 1 ends on the first Thursday of the year; each next period is +7 days.
 */
export function getReportingPeriodRange(year: number, periodIndex: number): ReportingPeriodRange {
  const firstThursday = getFirstThursdayOfYear(year);
  const endDate = addDays(firstThursday, (periodIndex - 1) * 7);
  const startDate = subDays(endDate, 6);

  return {
    periodIndex,
    start: format(startDate, 'yyyy-MM-dd'),
    end: format(endDate, 'yyyy-MM-dd'),
  };
}

/**
 * Returns the latest reporting period index whose Thursday end date is on or before `asOfDate`.
 * Caps at 53 periods per year.
 */
export function getLatestCompletedReportingPeriodIndex(year: number, asOfDate: Date = new Date()): number {
  let periodIndex = 1;

  while (periodIndex <= 53) {
    const { end } = getReportingPeriodRange(year, periodIndex);
    const endDate = parseISO(`${end}T00:00:00`);

    if (endDate > asOfDate) {
      return Math.max(1, periodIndex - 1);
    }

    periodIndex++;
  }

  return 53;
}

/** Returns the reporting period index that contains the given ISO date (`yyyy-MM-dd`). */
export function getReportingPeriodIndexForDate(year: number, isoDate: string): number {
  let periodIndex = 1;

  while (periodIndex <= 53) {
    const { end } = getReportingPeriodRange(year, periodIndex);
    const endDate = parseISO(`${end}T00:00:00`);
    const targetDate = parseISO(`${isoDate}T00:00:00`);

    if (targetDate <= endDate) {
      return periodIndex;
    }

    periodIndex++;
  }

  return 53;
}

/** Returns period indices 1..N through the period that contains `isoDate` (for YTD setups). */
export function getReportingPeriodIndicesThroughDate(year: number, isoDate: string): number[] {
  const lastPeriodIndex = getReportingPeriodIndexForDate(year, isoDate);
  return Array.from({ length: lastPeriodIndex }, (_, index) => index + 1);
}

/** Returns period indices 1..N for all completed reporting periods as of `asOfDate`. */
export function getReportingPeriodIndices(year: number, asOfDate: Date = new Date()): number[] {
  const latestPeriodIndex = getLatestCompletedReportingPeriodIndex(year, asOfDate);
  return Array.from({ length: latestPeriodIndex }, (_, index) => index + 1);
}

/** Formats a date range with full dates, e.g. "Dec 26, 2025 - Jan 1, 2026". */
export function formatFullDateRange(startIsoDate: string, endIsoDate: string): string {
  const startDate = parseISO(`${startIsoDate}T00:00:00`);
  const endDate = parseISO(`${endIsoDate}T00:00:00`);
  const fullDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${fullDate.format(startDate)} - ${fullDate.format(endDate)}`;
}

/**
 * Formats a date range for column captions.
 * Same month → "Jan 2026"; same year → "Jan 2 - Jan 8, 2026"; otherwise full dates.
 */
export function formatCompactDateRange(startIsoDate: string, endIsoDate: string): string {
  const startDate = parseISO(`${startIsoDate}T00:00:00`);
  const endDate = parseISO(`${endIsoDate}T00:00:00`);
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  const shortMonthDay = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const shortMonthDayYear = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const shortMonthYear = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
  });

  if (sameMonth) {
    return shortMonthYear.format(startDate);
  }

  if (sameYear) {
    return `${shortMonthDay.format(startDate)} - ${shortMonthDayYear.format(endDate)}`;
  }

  return `${shortMonthDayYear.format(startDate)} - ${shortMonthDayYear.format(endDate)}`;
}

/** Builds a dual-year column caption with current range on line 1 and compare range on line 2. */
export function formatReportingPeriodColumnCaption(
  currentRange: Pick<ReportingPeriodRange, 'start' | 'end'>,
  compareRange: Pick<ReportingPeriodRange, 'start' | 'end'>,
): string {
  return `${formatFullDateRange(currentRange.start, currentRange.end)} /\n${formatFullDateRange(compareRange.start, compareRange.end)}`;
}
